import type { UserHydraItem } from "@verbleif/lib/src/composables/Api/Type/User/user.interface";
import type { AuthResponse } from "@verbleif/shared/src/composables/Authentication/Interface/AuthResponse.interface";
import * as Sentry from "@sentry/vue";
import i18n from "@verbleif/lib/src/translations/i18n";
import { updateLocale } from "@verbleif/lib/src/translations/localeState";
import { ACTIVE_ACCOUNT_SESSION_KEY, AUTHUSER_QUERY_PARAM, useAccountsStore } from "@verbleif/shared/src/composables/AccountsStore/useAccountsStore";
import { fetchCurrentUserWithFreshToken } from "@verbleif/shared/src/composables/AuthStore/currentUser.api";
import { useAuthStore } from "@verbleif/shared/src/composables/AuthStore/useAuthStore";
import { useClientSettingsStore } from "@verbleif/shared/src/composables/ClientSettingsStore/useClientSettingsStore";
import { useLocationStore } from "@verbleif/shared/src/composables/LocationStore/useLocationStore";
import { permissionService } from "@verbleif/shared/src/composables/Permissions/SharedPermissions";
import { fetchResource } from "@verbleif/shared/src/composables/Query/resourceQuery";
import { ensureLoginCatalog } from "@verbleif/shared/src/composables/Query/useResourceCatalog";
import { refreshEffectiveUserPreferences } from "@verbleif/shared/src/composables/Users/UserPreferences/refreshEffectiveUserPreferences";
import router from "@web/core/plugin/router";
import { showPendingAccountSwitchToast } from "@web/features/Auth/Accounts/accountSwitchToast";
import Gleap from "gleap";

type CurrentUser = UserHydraItem & {
  gleapUserHash: string
};

/**
 * Loads everything that depends on who the API sees as the current user: the user, the login
 * catalog, the effective preferences, permissions and client settings.
 *
 * `freshToken`: the token pair was just issued. A 401 on the user is then reported to the caller
 * instead of starting another refresh, which inside a refresh would wait on itself forever.
 */
export async function loadSession({ freshToken = false }: { freshToken?: boolean } = {}): Promise<CurrentUser> {
  const authStore = useAuthStore();

  const user = freshToken
    ? await fetchCurrentUserWithFreshToken<CurrentUser>()
    : await fetchResource<CurrentUser>("/api/users/me");

  // The locale change resets the query cache. Apply it before the catalog loads: the reset the user watch in App.vue
  // would otherwise start on the next tick cancels the catalog fetches below and leaves locations and users empty.
  if (user.locale) {
    updateLocale(user.locale.toLowerCase());
  }

  authStore.setUser(user);

  await ensureLoginCatalog({ retry: false });

  await refreshEffectiveUserPreferences();

  permissionService.init();

  await useClientSettingsStore().load();

  return user;
}

/** Stores a new token pair; the refresh plugin calls it before anything waits on the new tokens. */
export function storeTokens(response: AuthResponse): void {
  const authStore = useAuthStore();
  authStore.setToken(response.access_token);
  authStore.setRefreshToken(response.refresh_token, true);
}

export async function onAuthenticateSuccess(response: AuthResponse, isLogin: boolean = false): Promise<void> {
  const locationStore = useLocationStore();
  storeTokens(response);

  const user = await loadSession({ freshToken: true });

  const accountsStore = useAccountsStore();
  const knownIndex = accountsStore.upsertActive({
    userId: user.id,
    name: user.name,
    username: user.username,
    avatarUrl: user.avatar?.contentUrl ?? null,
  });
  if (knownIndex !== accountsStore.activeIndex) {
    // Signed in again as a user that already occupies another slot: hand the fresh token to that
    // slot and reload as that account. The tokens and the user set above went into the reserved
    // slot, so it is wiped to keep it free for a genuinely new account.
    accountsStore.writeRefreshToken(response.refresh_token, knownIndex);
    accountsStore.removeAccount(accountsStore.activeIndex);
    sessionStorage.setItem(ACTIVE_ACCOUNT_SESSION_KEY, String(knownIndex));
    window.location.replace(`/?${AUTHUSER_QUERY_PARAM}=${knownIndex}`);
    return;
  }

  showPendingAccountSwitchToast();

  try {
    if (user.gleapUserHash) {
      Gleap.identify(user.id.toString(), {
        name: user.username,
        email: user.username,
        avatar: user.avatar?.contentUrl || undefined,
        companyName: locationStore.selectedClient.value?.name,
        customData: {
          platform: "web",
        },
      }, user.gleapUserHash);
      Gleap.setTags(["web"]);
      Gleap.showFeedbackButton(true);
      Gleap.setLanguage(i18n.global.locale.value ?? "nl");
    }
  } catch (e) {
    Sentry.captureException(e);
    console.error("Gleap:", e);
  }

  try {
    if (import.meta.env.PROD) {
      Sentry.setUser(user);
    }
  } catch (e) {
    console.error("Sentry:", e);
  }

  if (isLogin) {
    const { returnUrl } = useAuthStore();

    if (returnUrl.value) {
      await router.push(returnUrl.value);
    } else {
      await router.push({ name: "tasks" });
    }
  }
}
